import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { buttonStyles } from '../css/commonStyles';
import {
  colors,
  newRequestBtnSx,
  pageTitleSx,
  tabHeaderRowSx,
  tableAlertErrorSx,
  tableCardStyles,
  tableContainerStyles,
} from '../css/revenueItems.styles';
import { getDistributionBatches, cancelDistributionBatch } from 'helpers/distributionBatch.api';
import { formatBatchPeriod, formatRevenueSharingErrorMessage, isPendingDistributionBatch } from '../utils/revenueSharing.utils';
import { buildPaginationItems, REVENUE_ITEMS_PAGE_SIZE } from '../utils/revenueItems.utils';
import { revenueSharingMessage } from '../utils/revenueSharingMessage';
import BatchStatusBadge from './BatchStatusBadge';
import CreateBatchModal from './CreateBatchModal';
import VaultNotConfiguredBanner from './VaultNotConfiguredBanner';
import PaymentsPagination from './PaymentsPagination';

const RevenueSharingSection = ({ currentDao, vaultAddress, onGoToConfiguration }) => {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: REVENUE_ITEMS_PAGE_SIZE,
    totalItems: 0,
    totalPages: 1,
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedBatchId, setSelectedBatchId] = useState(null);
  const [executeOnly, setExecuteOnly] = useState(false);
  const [viewOnly, setViewOnly] = useState(false);
  const handleModalSuccess = async (result) => {
    await fetchBatches();
    if (!result?.action) return;

    const { action, message: detail, severity } = result;
    const showError = (text) => {
      if (severity === 'warning') revenueSharingMessage.warning(text);
      else revenueSharingMessage.error(text);
    };

    switch (action) {
      case 'distributed':
        revenueSharingMessage.success('Revenues distributed successfully!');
        break;
      case 'distribute_failed':
        showError(detail || 'Distribution failed. Please try again.');
        break;
      case 'saved':
        revenueSharingMessage.success('Revenue sharing cycle saved successfully!');
        break;
      case 'save_failed':
        showError(detail || 'Save failed. Please try again.');
        break;
      default:
        break;
    }
  };

  const hasVault = /^0x[0-9a-fA-F]{40}$/.test((vaultAddress || currentDao?.vaultAddress || '').trim());

  const fetchBatches = useCallback(async () => {
    if (!currentDao?._id) return;
    setLoading(true);
    setError('');
    try {
      const res = await getDistributionBatches({
        daoId: currentDao._id,
        page,
        limit: REVENUE_ITEMS_PAGE_SIZE,
      });
      setBatches(res?.data || []);
      setPagination({
        page: res?.pagination?.page || page,
        limit: res?.pagination?.limit || REVENUE_ITEMS_PAGE_SIZE,
        totalItems: res?.pagination?.totalItems || 0,
        totalPages: res?.pagination?.totalPages || 1,
      });
    } catch (err) {
      setBatches([]);
      setPagination({ page: 1, limit: REVENUE_ITEMS_PAGE_SIZE, totalItems: 0, totalPages: 1 });
      setError(err?.message || 'Failed to load distribution batches');
    } finally {
      setLoading(false);
    }
  }, [currentDao?._id, page]);

  useEffect(() => {
    setPage(1);
  }, [currentDao?._id]);

  useEffect(() => {
    fetchBatches();
  }, [fetchBatches]);

  const paginationItems = useMemo(
    () => buildPaginationItems(pagination.page || 1, pagination.totalPages || 1),
    [pagination.page, pagination.totalPages],
  );

  const paginationLabel = useMemo(() => {
    if (!pagination.totalItems) {
      return loading ? 'Loading revenue sharing cycles...' : 'No revenue sharing cycles yet';
    }
    const start = (pagination.page - 1) * pagination.limit + 1;
    const end = Math.min(start + batches.length - 1, pagination.totalItems);
    return `Showing ${start}-${end} of ${pagination.totalItems} cycles`;
  }, [loading, pagination, batches.length]);

  const closeModal = () => {
    setModalOpen(false);
    setSelectedBatchId(null);
    setExecuteOnly(false);
    setViewOnly(false);
  };

  const openNew = () => {
    setSelectedBatchId(null);
    setExecuteOnly(false);
    setViewOnly(false);
    setModalOpen(true);
  };

  const openView = (id) => {
    setSelectedBatchId(id);
    setExecuteOnly(false);
    setViewOnly(true);
    setModalOpen(true);
  };

  const openExecute = (id) => {
    setSelectedBatchId(id);
    setExecuteOnly(true);
    setViewOnly(false);
    setModalOpen(true);
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this batch and release reserved revenue items?')) return;
    try {
      await cancelDistributionBatch(id);
      await fetchBatches();
      revenueSharingMessage.success('Revenue sharing cycle cancelled successfully.');
    } catch (err) {
      revenueSharingMessage.error(formatRevenueSharingErrorMessage(err, 'Failed to cancel batch'));
    }
  };

  const renderActions = (row) => {
    const { status } = row;
    if (isPendingDistributionBatch(status)) {
      return (
        <>
          <Button size="small" onClick={() => openExecute(row._id)} sx={{ textTransform: 'none', color: colors.accent }}>
            Distribute on-chain
          </Button>
          <Button size="small" onClick={() => handleCancel(row._id)} sx={{ textTransform: 'none', color: '#ff3b30' }}>
            Cancel
          </Button>
        </>
      );
    }
    if (status === 'executed') {
      return (
        <Button size="small" onClick={() => openView(row._id)} sx={{ textTransform: 'none', color: colors.textSecondary }}>
          View
        </Button>
      );
    }
    return null;
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={tabHeaderRowSx}>
        <Typography sx={{ ...pageTitleSx, fontSize: '1.35rem' }}>Revenue sharing</Typography>
        <Button
          variant="contained"
          onClick={openNew}
          disabled={!hasVault}
          sx={{ ...buttonStyles, ...newRequestBtnSx }}
        >
          New cycle
        </Button>
      </Box>

      {!hasVault && (
        <VaultNotConfiguredBanner
          message="Vault address is not configured. Please set it up before creating a revenue sharing cycle."
          actionLabel={onGoToConfiguration ? 'Update Vault Address' : undefined}
          onAction={onGoToConfiguration}
        />
      )}

      {error && (
        <Alert severity="error" sx={tableAlertErrorSx}>
          {error}
        </Alert>
      )}

      <Box sx={tableCardStyles}>
        <TableContainer component={Box} sx={tableContainerStyles}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Cycle period</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Total revenue (USDT)</TableCell>
                <TableCell>Split version</TableCell>
                <TableCell align="center">Revenue items</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                    <CircularProgress size={24} sx={{ color: colors.accent }} />
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                batches.map((row) => (
                  <TableRow key={row._id} hover>
                    <TableCell>{formatBatchPeriod(row.periodStart, row.periodEnd)}</TableCell>
                    <TableCell>
                      <BatchStatusBadge status={row.status} />
                    </TableCell>
                    <TableCell align="right">{row.totalRevenue}</TableCell>
                    <TableCell>v{row.splitVersion}</TableCell>
                    <TableCell align="center">{row.revenueItemIds?.length || 0}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'nowrap' }}>
                        {renderActions(row)}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              {!loading && !batches.length && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 5, color: colors.textSecondary }}>
                    No revenue sharing cycles yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <PaymentsPagination
          pagination={pagination}
          paginationItems={paginationItems}
          paginationLabel={paginationLabel}
          onPageChange={setPage}
        />
      </Box>

      <CreateBatchModal
        open={modalOpen}
        onClose={closeModal}
        currentDao={currentDao}
        vaultAddress={vaultAddress}
        existingBatchId={selectedBatchId}
        executeOnly={executeOnly}
        viewOnly={viewOnly}
        onSuccess={handleModalSuccess}
      />
    </Box>
  );
};

export default RevenueSharingSection;
